import {HttpErrorResponse, HttpResponse, HttpResponseBase} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '../gen/api/authentication.service';
import { AuthenticationFlowService } from '../gen/api/authenticationFlow.service';
import { AuthenticationChallenge } from '../gen/model/authenticationChallenge';
import { AuthenticationChallenges } from '../gen/model/authenticationChallenges';
import { AuthenticationFlows } from '../gen/model/authenticationFlows';
import { Logger } from '../util/logger';
import ResponseUtil from '../util/responseUtil';
import {Credentials} from '../gen/model/credentials';
import {UserForRegisterUser} from '../gen/model/userForRegisterUser';
import {UserService} from '../gen/api/user.service';
import {User} from '../gen/model/user';
import {AcceptInvitationChallenge, SessionService} from '../gen';
import AuthenticationUtils from '../util/AuthenticationUtils';
import {ParamsService} from './params.service';
import {AppConstants} from './AppConstants';
import {CustomAcceptInvitationAuthenticationChallenge} from '../CustomAcceptInvitationAuthenticationChallenge';
const cloneDeep = require('lodash.clonedeep');

export interface AuthenticationInfo {
  /** Current user authentications */
  existingAuthentications: Array<AuthenticationChallenge>;
  /** Additional authentications required by the default/active authentication flow */
  requiredAuthentications: Array<AuthenticationChallenge>;
  /** Default authentication flows, or authentication flows available to the user if there is an existing session user */
  authenticationFlows: AuthenticationFlows;
  /** Unhandled optional authentication challenges */
  optionalAuthentications: Array<AuthenticationChallenge>;
}
export interface SessionInfo {
  hasSession: boolean;
  fullyAuthenticated: boolean;
  optionalsHandled: boolean;
  tasksInProgress: Array<string>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationInfoService {

  private profileSubscription: BehaviorSubject<User> = new BehaviorSubject<User>(undefined);

  private skippedInvitations: Array<string> = [];

  public authenticationInfo: BehaviorSubject<AuthenticationInfo> = new BehaviorSubject<AuthenticationInfo>(undefined);
  // Todo: rename uistate
  public sessionInfo: BehaviorSubject<SessionInfo> = new BehaviorSubject<SessionInfo>( {
    hasSession: false,
    fullyAuthenticated: false,
    tasksInProgress: [],
    optionalsHandled: false,
  });

  constructor(
    @Inject('AuthenticationApi') private authenticationApi: AuthenticationService,
    @Inject('AuthenticationFlowApi') private authenticationFlowApi: AuthenticationFlowService,
    @Inject('UserApi') private userApi: UserService,
    @Inject('SessionApi') private sessionApi: SessionService,
    private logger: Logger) { }

  public startTask(taskId: string) {
    this.logger.debug('started: %s', taskId);
    const newInfo =  cloneDeep(this.sessionInfo.value);
    newInfo.tasksInProgress.push(taskId);
    this.sessionInfo.next(newInfo);
  }
  public completeTask(taskId: string) {
    this.logger.debug('completed: %s', taskId);
    const newInfo = cloneDeep(this.sessionInfo.value);
    if (newInfo.tasksInProgress.indexOf(taskId) !== newInfo.tasksInProgress.length - 1) {
      throw new Error('AuthenticationInfoService.completeTask: Task not in progress');
    } else {
      newInfo.tasksInProgress.pop();
      this.sessionInfo.next(newInfo);
    }
  }
  public cancelTask(taskId: string) {
    this.logger.debug('cancelled: %s', taskId);
    const newInfo = cloneDeep(this.sessionInfo.value);
    if (newInfo.tasksInProgress.indexOf(taskId) !== newInfo.tasksInProgress.length - 1) {
      throw new Error('AuthenticationInfoService.cancelTask: Task not in progress');
    } else {
      newInfo.tasksInProgress.pop();
      this.sessionInfo.next(newInfo);
    }
  }
  public skipInvitation(id: string) {
    if (this.skippedInvitations.indexOf(id) < 0) {
      this.skippedInvitations.push(id);
      this.fetchAuthenticationInfo();
    }
  }
  private clearSkippedInvitations() {
    if (this.skippedInvitations.length) {
      this.skippedInvitations = [];
    }
  }
  public getUser(): Observable<HttpResponse<any>> {
    return new Observable<HttpResponse<any>>(subscriber => {
      const t = this.userApi.getUser('response');
      t.subscribe((resp) => {
        subscriber.next(resp);
        if (resp && resp.body) {
          this.profileSubscription.next(resp.body as User);
        } else {
          this.profileSubscription.next(null);
        }
      }, (err) => {
        subscriber.error(err);
        this.profileSubscription.next(null);
      });
    });
  }
  public getProfileSubscription(): BehaviorSubject<User> {
    return this.profileSubscription;
  }
  public updateUser(body: User): Observable<HttpResponse<any>> {
    return this.userApi.updateUser(body, 'response');
  }

  public registerUser(
    body: UserForRegisterUser,
    verifyEmail?: boolean,
    verifyEmailUri?: string,
    remember?: boolean
  ): Observable<HttpResponse<any>> {
    return new Observable<HttpResponse<any>>(subscriber => {
      const t: Observable<HttpResponse<any>> = this.userApi.registerUser(body, verifyEmail, verifyEmailUri, remember, 'response');
      t.subscribe(resp => {
        subscriber.next(resp);
        this.fetchAuthenticationInfo();
      }, err => {
        subscriber.error(err);
        this.fetchAuthenticationInfo();
      });
    });
  }

  public endSession(ignoreConsumerSingleSignOut?: boolean, ignoreProviderSingleSignOut?: boolean): Observable<HttpResponse<any>> {
    return new Observable<HttpResponse<any>>(subscriber => {
      const t: Observable<HttpResponse<any>> = this.sessionApi.endSession(
        ignoreConsumerSingleSignOut,
        ignoreProviderSingleSignOut,
        'response'
      );
      t.subscribe(response => {
        subscriber.next(response);
        this.clearSkippedInvitations();
        this.fetchAuthenticationInfo();
      }, err => {
        subscriber.error(err);
        this.clearSkippedInvitations();
        this.fetchAuthenticationInfo();
      });
    });
  }
  public addAuthentications(credentials: Credentials, remember?: boolean): Observable<HttpResponse<any>> {
    return new Observable<HttpResponse<any>>(subscriber => {
      const t: Observable<HttpResponse<any>> = this.authenticationApi.addAuthentications(credentials, remember, 'response');
      t.subscribe(response => {
        this.fetchAuthenticationInfo(null, () => {
          subscriber.next(response);
        });
      }, err => {
        subscriber.error(err);
        this.fetchAuthenticationInfo();
      });
    });
  }
  public fetchAuthenticationInfo(username?: string, callback?: (val?: AuthenticationInfo, ses?: SessionInfo) => void) {

    const listExistingAuthenticationsRequest: Observable<HttpResponseBase> =
      this.authenticationApi.listAuthentications('response')
        .pipe(catchError(error => ResponseUtil.handleAuthenticationChallengeResponse(error)));

    const listUserAuthenticationFlowsRequest: Observable<HttpResponse<any>> =
      this.authenticationFlowApi.listUserAuthenticationFlows(username || '', 'response');

    const bundledRequests = forkJoin([listExistingAuthenticationsRequest, listUserAuthenticationFlowsRequest]);

    bundledRequests.subscribe(responses => {
      // first handle the listUserAuthenticationFlowsRequest
      const listUserAuthenticationFlowsResponse: any = responses[1];
      const userAuthenticationFlows: AuthenticationFlows = listUserAuthenticationFlowsResponse.body;
      this.logger.debug('Users authentication flows: %O', userAuthenticationFlows || []);

      // second handle the listExistingAuthenticationsRequest
      const listExistingAuthenticationsResponse = responses[0];
      let existingAuthenticationObjs: AuthenticationChallenges = null;

      if (listExistingAuthenticationsResponse instanceof HttpResponse) {
        existingAuthenticationObjs = (listExistingAuthenticationsResponse as HttpResponse<AuthenticationChallenges>).body;
      } else if (listExistingAuthenticationsResponse instanceof HttpErrorResponse) {
        existingAuthenticationObjs = (listExistingAuthenticationsResponse as HttpErrorResponse).error as AuthenticationChallenges;
      } else {
        existingAuthenticationObjs = [];
      }
      this.logger.debug('Existing authentications: %O', existingAuthenticationObjs);

      let challenges = null;
      const additionalRequirement = ParamsService.getParamSync(AppConstants.QP_REQUIRE_CHALLENGE);
      let additionalChallenge = null;
      if (existingAuthenticationObjs && existingAuthenticationObjs.length) {
        const nextChallenge: AuthenticationChallenge = AuthenticationUtils.findNextChallenge(
          {
            authenticationFlows: userAuthenticationFlows,
            existingAuthentications: existingAuthenticationObjs,
            requiredAuthentications: null,
            optionalAuthentications: null,
          } as AuthenticationInfo
        );
        const req = [];
        const opt = [];
        if (additionalRequirement === AppConstants.AC_AM_INVITATION_ACCEPTED) {
          additionalChallenge = {
            token: ParamsService.getParamSync(AppConstants.QP_INVITATION_TOKEN),
            email: ParamsService.getParamSync(AppConstants.QP_EMAIL),
            authenticationMethod: AppConstants.AC_AM_INVITATION_ACCEPTED,
          } as CustomAcceptInvitationAuthenticationChallenge;
        } else if (additionalRequirement) {
          throw new Error('AuthenticationInforService.fetchAuthenticationInfo: Unsupported additional requirement ' +
            additionalRequirement
          );
        }
        let additionalRequirementAdded = false;
        if (nextChallenge) {
          let chall = nextChallenge;
          while (chall) {
            if (additionalRequirement === AppConstants.AC_AM_INVITATION_ACCEPTED &&
                chall.authenticationMethod === AppConstants.AC_AM_INVITATION
            ) {
              req.push(additionalChallenge);
              additionalRequirementAdded = true;
            }
            if (chall.authenticationMethod !== AppConstants.AC_AM_INVITATION || (this.skippedInvitations.indexOf(
                (chall as AcceptInvitationChallenge).invitationId) < 0 && (chall as AcceptInvitationChallenge).verified)) {
              if (chall.required) {
                req.push(chall);
              } else {
                opt.push(chall);
              }
            }
            //
            chall = chall.challenges ? chall.challenges[0] : null;
          }
          if (!additionalRequirementAdded && additionalRequirement) {
            req.push(additionalChallenge);
          }
        } else if (additionalRequirement) {
          req.push(additionalChallenge);
        }
        challenges = {
          optional: opt,
          required: req,
        };
      } else {
        challenges = {
          required: [],
          optional: [],
        };
      }

      this.logger.debug('Required authentications: %O', challenges.required || []);
      this.logger.debug('Optional authentications: %O', challenges.optional || []);
      const authnInfo: AuthenticationInfo = {
        authenticationFlows: userAuthenticationFlows,
        existingAuthentications: existingAuthenticationObjs,
        requiredAuthentications: challenges.required,
        optionalAuthentications: challenges.optional,
      };
      const sessInfo: SessionInfo = {
        hasSession: existingAuthenticationObjs.length > 0,
        fullyAuthenticated: existingAuthenticationObjs.length > 0 && (!challenges.required || challenges.required.length === 0),
        tasksInProgress: [].concat(this.sessionInfo.value.tasksInProgress),
        optionalsHandled: challenges.optional.length === 0,
      };
      this.logger.debug(
        'Users authentication status:\n - hasSession: %O \n - fullyAuthenticated: %O \n - optionalsHandled: %O',
        sessInfo.hasSession,
        sessInfo.fullyAuthenticated,
        sessInfo.optionalsHandled)
      ;
      this.authenticationInfo.next(authnInfo);
      this.sessionInfo.next(sessInfo);
      if (callback) {
        callback(authnInfo, sessInfo);
      }
    });
  }
}
